"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useSearchParams } from "next/navigation";
import { useTheme } from "next-themes";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import LogoText from "@/components/global/LogoText";
import LoadingSpinner from "@/components/loading-spinner/loading-spinner";
import FlickeringGrid from "@/components/magicui/flickeringGrid";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { useRouter } from "@/lib/i18n";
import { Link } from "@/lib/i18n";
import { mixpanelSignin } from "@/lib/tracker";
import { cn } from "@/lib/utils";
import * as m from "@/paraglide/messages";
import { check2fa } from "@/services/controllers/user";
import { useAuthHook } from "@/services/hooks/auth.hooks";
import { useAppDispatch } from "@/services/hooks/redux.hooks";
import { set2fa } from "@/services/redux/user.slice";

import { loginWithCredential } from "../api";

const scheme = z.object({
  email: z.string().email(),
  password: z.string(),
});

const LoginPage = () => {
  const { theme } = useTheme();
  const { push } = useRouter();
  const searchParam = useSearchParams();
  const dispatch = useAppDispatch();
  const { authError, loginWithPassword, signinWithGoogle, signinWithApple } =
    useAuthHook();

  const [loading, setLoading] = useState<boolean>(false);
  const [loadingGoogle, setLoadingGoogle] = useState<boolean>(false);
  const [loadingApple, setLoadingApple] = useState<boolean>(false);
  const redirectTo = searchParam.get("redirect");

  const form = useForm<z.infer<typeof scheme>>({
    resolver: zodResolver(scheme),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const handleSuccessfulAuth = useCallback(
    async (cred: any) => {
      await loginWithCredential(cred);

      const {
        data: { data },
      } = await check2fa({
        params: {
          email: cred.user.email,
        },
      });

      // Update Redux state with 2FA status
      dispatch(set2fa(data));

      if (data) {
        if (redirectTo != null) {
          push(
            "/signin/2fa-confirmation?redirect=" +
              decodeURIComponent(redirectTo),
          );
        } else {
          push("/signin/2fa-confirmation");
        }
      } else {
        if (redirectTo != null)
          push(
            redirectTo.includes("?")
              ? decodeURIComponent(redirectTo).replace("-", "&")
              : decodeURIComponent(redirectTo),
          );
        else push("/dashboard");
      }
    },
    [push, redirectTo],
  );

  const onLogin = useCallback(
    async ({ email, password }: { email: string; password: string }) => {
      setLoading(true);
      try {
        const cred = await loginWithPassword(email.trim(), password.trim());

        if (cred != null) {
          mixpanelSignin("email", cred.user);
          await handleSuccessfulAuth(cred);
        } else {
          form.setError(`root`, {
            message: authError,
          });
        }
      } catch (error) {
        form.setError(`root`, {
          message: authError || "Login failed. Please try again.",
        });
      } finally {
        setLoading(false);
      }
    },
    [authError, form, handleSuccessfulAuth, loginWithPassword],
  );

  const onLoginWithGoogle = useCallback(async () => {
    setLoadingGoogle(true);
    try {
      const cred: any = await signinWithGoogle();

      if (cred != null) {
        mixpanelSignin("google", cred.user);
        await handleSuccessfulAuth(cred);
      } else {
        form.setError(`root`, {
          message: authError,
        });
      }
    } catch (error: any) {
      // Handle specific popup errors
      if (
        error.code?.includes("auth/popup") ||
        error.message?.includes("Cross-Origin-Opener-Policy") ||
        error.message?.includes("popup") ||
        error.code?.includes("cancelled") ||
        error.code?.includes("closed")
      ) {
        form.setError(`root`, {
          message:
            "Popup was blocked. Please try again or use another sign-in method.",
        });
      } else {
        form.setError(`root`, {
          message: authError || "Sign in with Google failed. Please try again.",
        });
      }
    } finally {
      setLoadingGoogle(false);
    }
  }, [authError, form, handleSuccessfulAuth, signinWithGoogle]);

  const onLoginWithApple = useCallback(async () => {
    setLoadingApple(true);
    try {
      const cred: any = await signinWithApple();

      if (cred != null) {
        mixpanelSignin("apple", cred.user);
        await handleSuccessfulAuth(cred);
      } else {
        form.setError(`root`, {
          message: authError,
        });
      }
    } catch (error: any) {
      // Handle specific popup errors
      if (
        error.code?.includes("auth/popup") ||
        error.message?.includes("Cross-Origin-Opener-Policy") ||
        error.message?.includes("popup") ||
        error.code?.includes("cancelled") ||
        error.code?.includes("closed")
      ) {
        form.setError(`root`, {
          message:
            "Popup was blocked. Please try again or use another sign-in method.",
        });
      } else {
        form.setError(`root`, {
          message: authError || "Sign in with Apple failed. Please try again.",
        });
      }
    } finally {
      setLoadingApple(false);
    }
  }, [authError, form, handleSuccessfulAuth, signinWithApple]);

  useEffect(() => {
    if (authError !== "") {
      form.setError(`root`, {
        message: authError,
      });
      setLoading(false);
      setLoadingGoogle(false);
      setLoadingApple(false);
    }
  }, [authError, form]);

  return (
    <div className="w-full lg:grid lg:min-h-[600px] lg:grid-cols-1 xl:min-h-[800px] h-screen">
      <FlickeringGrid
        className={cn(
          "[mask-image:radial-gradient(circle_at_top_left,white,transparent)] z-0 absolute inset-0 size-full",
        )}
        squareSize={2}
        gridGap={8}
        color={theme === "light" ? "#333" : "#fff"}
        maxOpacity={0.2}
        flickerChance={0.4}
      />
      <div className="h-screen flex items-center justify-center py-12 md:px-0">
        <div className="z-10 flex flex-col gap-4 items-center">
          <Link href="/">
            <LogoText className="h-8" />
          </Link>
          <Card className="mx-auto bg-white dark:bg-background z-10 max-w-sm w-[24rem]">
            <CardHeader>
              <CardTitle className="text-2xl">{m.login()}</CardTitle>
              <CardDescription>
                {m.sad_inclusive_giraffe_emerge()}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onLogin)}
                  className="grid gap-4"
                >
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input placeholder="name@example.com" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center">
                          <FormLabel>{m.password()}</FormLabel>
                          <Link
                            href="/reset-password"
                            className="ml-auto inline-block text-sm underline"
                          >
                            {m.forgot_password()}
                          </Link>
                        </div>
                        <FormControl>
                          <Input placeholder="" {...field} type="password" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {form.formState.errors.root ? (
                    <p className="text-[0.8rem] font-medium text-destructive">
                      {form.formState.errors.root?.message ?? ""}
                    </p>
                  ) : (
                    <></>
                  )}
                  <Button
                    type="submit"
                    className="w-full mt-4"
                    disabled={loadingGoogle || loading || loadingApple}
                  >
                    {loading ? <LoadingSpinner /> : m.login()}
                  </Button>
                </form>
              </Form>

              <div className="relative flex items-center overflow-hidden max-w-full h-fit my-6">
                <Separator className="w-[44%]" />
                <span className="text-sm text-muted-foreground px-2 bg-background dark:bg-transparent">
                  {m.or()}
                </span>
                <Separator className="w-[44%]" />
              </div>

              <Button
                variant="outline"
                className="w-full hover:bg-gray-100 dark:hover:bg-slate-900"
                onClick={onLoginWithGoogle}
                disabled={loadingGoogle || loading || loadingApple}
              >
                {loadingGoogle ? (
                  <LoadingSpinner />
                ) : (
                  <>
                    <GoogleIcon /> {m.signin_google()}
                  </>
                )}
              </Button>

              <Button
                variant="outline"
                className="w-full hover:bg-gray-100 dark:hover:bg-slate-900 mt-2"
                onClick={onLoginWithApple}
                disabled={loadingGoogle || loadingApple || loading}
              >
                {loadingApple ? (
                  <LoadingSpinner />
                ) : (
                  <>
                    <AppleIcon /> {m.signin_apple()}
                  </>
                )}
              </Button>

              <div className="mt-4 text-center text-sm">
                {m.no_account()}{" "}
                <Link
                  href={
                    redirectTo != null
                      ? `/signup?'redirect=${decodeURIComponent(redirectTo)}'`
                      : "/signup"
                  }
                  className="underline"
                >
                  {m.signup()}
                </Link>
              </div>
            </CardContent>
          </Card>
          <Link href="/">
            <p className="text-xs text-muted-foreground ">
              {m.gross_stock_ostrich_spark()}
            </p>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;

const GoogleIcon = () => (
  <svg
    className="mr-2 h-4 w-4"
    aria-hidden="true"
    focusable="false"
    data-prefix="fab"
    data-icon="google"
    role="img"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 488 512"
  >
    <path
      fill="currentColor"
      d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 123 24.5 166.3 64.9l-67.5 64.9C258.5 52.6 94.3 116.6 94.3 256c0 86.5 69.1 156.6 153.7 156.6 98.2 0 135-70.4 140.8-106.9H248v-85.3h236.1c2.3 12.7 3.9 24.9 3.9 41.4z"
    ></path>
  </svg>
);

const AppleIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    className="fill-black dark:fill-white"
  >
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
  </svg>
);
